import classNames from 'classnames';
import React, { FC, useEffect } from 'react';

import { CurrencyInput } from '@eika/currency-input';
import { Icon, IconTypes } from '@eika/icon';
import { ErrorLabel, Label, WarningLabel } from '@eika/label';

import type { Currency } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { PaymentLimitsErrorMessage } from '../../../shared/PaymentLimitsErrorMessage';

import './AmountInput.scss';

export interface AmountInputProps {
    currency?: Currency;
    className?: string;
    value?: number;
    error?: string;
    limitError?: string;
    limitWarning?: string | null;
    locked?: boolean;
    isNoCoverage: boolean;
    domesticPaymentWarning?: boolean;
    label?: string;
    onChange: (value: number | undefined) => void;
    elementToFocusOnClose?: HTMLElement | null;
    infoMessage?: string;
    placeholder?: string;
}

const AmountInput: FC<AmountInputProps> = ({
    currency,
    className,
    value,
    label,
    error,
    limitError,
    limitWarning,
    onChange,
    isNoCoverage,
    locked = false,
    elementToFocusOnClose,
    domesticPaymentWarning,
    infoMessage,
    placeholder,
}) => {
    const translated = useTranslation();
    const targetCurrency = currency ?? 'NOK';

    useEffect(() => {
        return () => elementToFocusOnClose?.focus();
    }, [elementToFocusOnClose]);

    return (
        <div className={classNames('amount-input__container', className)}>
            <div className={classNames('amount-box', { 'amount-box__locked': locked })}>
                <label className="currency-input__wrapper">
                    <span id="currency-label">
                        <span className="nettbank-amount-label-text">{label}</span>
                        {locked && (
                            <span className="payment-padlock-icon">
                                <Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" />
                            </span>
                        )}
                    </span>

                    <CurrencyInput
                        decimalScale={2}
                        aria-labelledby="currency-label"
                        value={value}
                        maxLength={20}
                        name={label ?? ''}
                        suffix={targetCurrency === 'NOK' ? ' kr' : ` ${targetCurrency}`}
                        onValueChange={(e) => onChange(e.floatValue)}
                        decimalTrailingWithZeroes
                        disabled={locked}
                        placeholder={placeholder}
                        data-testid="amount-input"
                    />
                </label>
                {error && (
                    <div className="amount-box__minAmount">
                        <ErrorLabel icon={IconTypes.UTROPSTEGN_24} text={error} />
                    </div>
                )}
                {!error && limitError && !isNoCoverage && (
                    <div className="amount-box__minAmount">
                        <PaymentLimitsErrorMessage message={limitError} />
                    </div>
                )}
                {!error && limitWarning && !limitError && !isNoCoverage && (
                    <PaymentLimitsErrorMessage
                        className="amount-box__error-container"
                        message={limitWarning}
                        type="warning"
                    />
                )}
                {!error && !limitError && isNoCoverage && (
                    <div className="amount-box__error-container">
                        <WarningLabel text={translated.notEnoughInAccount} />
                    </div>
                )}
                {!error && !limitError && !isNoCoverage && !limitWarning && domesticPaymentWarning && (
                    <div className="amount-box__error-container">
                        <WarningLabel text={translated.crossBorderPayment.domesticPaymentWarning} />
                    </div>
                )}
                {!error && !limitError && !isNoCoverage && !limitWarning && !domesticPaymentWarning && infoMessage && (
                    <div className="amount-box__minAmount">
                        <Label text={infoMessage} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AmountInput;
